import { ReactNode, useEffect, useRef, useState } from "react";

import classes from "./ZoomablePanel.module.css";

type Props = {
  children: ReactNode;
};

type Transform = { scale: number; x: number; y: number };

const MIN_SCALE = 1;
const MAX_SCALE = 10;
const IDENTITY: Transform = { scale: MIN_SCALE, x: 0, y: 0 };

const clampTransform = ({ scale, x, y }: Transform, width: number, height: number): Transform => ({
  scale,
  x: Math.min(0, Math.max(width * (1 - scale), x)),
  y: Math.min(0, Math.max(height * (1 - scale), y)),
});

export const ZoomablePanel = ({ children }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerX: number; pointerY: number; origin: Transform } | null>(null);
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const { left, top, width, height } = container.getBoundingClientRect();
      const cursorX = event.clientX - left;
      const cursorY = event.clientY - top;
      const sensitivity = event.ctrlKey ? 0.01 : 0.003;

      setTransform((current) => {
        const scale = Math.min(
          MAX_SCALE,
          Math.max(MIN_SCALE, current.scale * Math.exp(-event.deltaY * sensitivity)),
        );
        const ratio = scale / current.scale;
        return clampTransform(
          {
            scale,
            x: cursorX - (cursorX - current.x) * ratio,
            y: cursorY - (cursorY - current.y) * ratio,
          },
          width,
          height,
        );
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (transform.scale === MIN_SCALE) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerX: event.clientX, pointerY: event.clientY, origin: transform };
    setIsDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const { width, height } = event.currentTarget.getBoundingClientRect();
    setTransform(
      clampTransform(
        {
          scale: drag.origin.scale,
          x: drag.origin.x + event.clientX - drag.pointerX,
          y: drag.origin.y + event.clientY - drag.pointerY,
        },
        width,
        height,
      ),
    );
  };

  const handlePointerEnd = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const isZoomed = transform.scale > MIN_SCALE;

  return (
    <div
      ref={containerRef}
      className={classes.viewport}
      data-zoomed={isZoomed}
      data-dragging={isDragging}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onDoubleClick={() => setTransform(IDENTITY)}
    >
      <div
        className={classes.content}
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
};
